// useOwnPostActions.ts — shared own-post edit/delete state + handlers for
// PostCard and HeroPostCard (they had ~90 duplicated lines of this each).
//
// Owns: isOwn detection (by id — see useIsOwn), the edit-dialog/sheet open state,
// the delete-confirm dialog open state, and the save/delete handlers that call
// postsService. Components remain responsible for their own JSX/layout.

import { useState } from 'react';
import type { Post } from '@/core/models/post';
import { useIsOwn, useSession } from '@/hooks';
import { useToast } from '@/ui';
import { postsService } from '@/core/services/postsService';

/** 1.23.00 Phase 4: free accounts get 30 minutes after a post PUBLISHES to
 * edit it; Pro is unlimited. Mirrors the server's EDIT_WINDOW exactly
 * (apps/content/api.py).
 *
 * STRICTLY ADVISORY. This is computed from the `premium` session mirror and
 * the browser's clock, so webhook lag, mirror drift or clock skew can all make
 * it disagree with the server. It therefore only decides whether the dialog
 * shows a heads-up NOTE — it never removes or disables Save. The server is the
 * only authority: postsService.editPost surfaces its `edit_window_expired`
 * rejection and `saveEdit` below reports that. A client must never be the
 * authority on an entitlement.
 *
 * Runs from `createdAt`, which the publish sweep resets to the real publish
 * moment for a scheduled post — same clock the server uses. An unpublished
 * post has no window (nothing to measure from yet — you can't be "past" a
 * window that hasn't started). Posts only — comments are unaffected. */
const EDIT_WINDOW_MS = 30 * 60 * 1000;

function computeEditWindowAdvisory(post: Post, isPremium: boolean): boolean {
  if (isPremium) return false;
  if (post.published === false) return false;
  if (!post.createdAt) return false;
  const age = Date.now() - new Date(post.createdAt).getTime();
  return age > EDIT_WINDOW_MS;
}

export interface OwnPostActionsHook {
  isOwn: boolean;
  editing: boolean;
  deleting: boolean;
  /** Best-guess signal that a free account's 30-minute edit window has passed
   * for this (published) post. ADVISORY ONLY — EditPostDialog uses it to show
   * a heads-up note above a still-enabled Save. Never gate the control on it;
   * see computeEditWindowAdvisory above. */
  editWindowAdvisory: boolean;
  openEdit: () => void;
  closeEdit: () => void;
  openDelete: () => void;
  closeDelete: () => void;
  /** Save an edit; closes the dialog on success, toasts on failure. */
  saveEdit: (body: string) => Promise<void>;
  /** Confirm the delete; closes the dialog either way, toasts on failure. */
  confirmDelete: () => Promise<void>;
}

export interface OwnPostActionsOptions {
  /** Called after a delete succeeds — for hosts showing a copy the post store doesn't hold. */
  onDeleted?: () => void;
}

export function useOwnPostActions(
  post: Post,
  { onDeleted }: OwnPostActionsOptions = {},
): OwnPostActionsHook {
  const isOwn = useIsOwn(post.authorId);
  const { isPremium } = useSession();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const editWindowAdvisory = computeEditWindowAdvisory(post, isPremium);

  async function saveEdit(body: string) {
    const result = await postsService.editPost(post.id, body);
    if (result === 'edit_window_expired') {
      // The ONLY authority on the edit window. Save stays enabled no matter
      // what the client guessed, so this is the path that actually tells a
      // member their window closed — the dialog deliberately stays open.
      toast('The 30-minute edit window has passed for this post. Pro removes the limit.');
      return;
    }
    if (result) setEditing(false);
    else toast('Could not save your edit');
  }

  async function confirmDelete() {
    const updated = await postsService.deletePost(post.id);
    setDeleting(false);
    if (!updated) toast('Could not delete post');
    else onDeleted?.();
  }

  return {
    isOwn,
    editing,
    deleting,
    editWindowAdvisory,
    openEdit: () => setEditing(true),
    closeEdit: () => setEditing(false),
    openDelete: () => setDeleting(true),
    closeDelete: () => setDeleting(false),
    saveEdit,
    confirmDelete,
  };
}
